import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ActivateStoreInput,
    DeactivateStoreInput,
    GraphDotnetService,
    graphql,
    MediaclipMutationActivateStoreAfterMigrationArgs,
    MediaclipMutationDeactivateStoreAndScheduleDeletionOfUserDataArgs,
    MediaclipMutationDeactivateStoreForMigrationArgs,
    MediaclipMutationSetStoreToMigratedArgs,
    MediaclipMutationUpdateStoreArgs,
    MediaclipMutationUpdateStoreConfigArgs,
    Store,
    UpdateStoreConfigInput,
    UpdateStoreInput,
} from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class StoresService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    getStore(storeId: string): Observable<Store> {
        return this.graphDotnetService.query(graphql`
                query getStore($storeId: String!) {
                    store(id: $storeId) {
                        id
                        subscriptionId
                        label
                        environment
                        status
                        deactivationDateUtc
                        migratedToRegion
                        automaticDesignerUpgrade
                        config {
                            renderingPriority
                        }
                        subscription {
                            id
                            label
                        }
                        metadata {
                            platform
                            plugin
                            ignoredProblems
                            testDesignerInfo {
                                url
                                description
                            }
                        }
                    }
                }
            `, { storeId }
        ).pipe(map(q => q.store));
    }

    updateStore(input: UpdateStoreInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreArgs>(graphql`
                mutation updateStore($input: UpdateStoreInput!) {
                    updateStore(input: $input)
                }
            `, { input: input }
        ).pipe(map(r => r.updateStore));
    }

    deactivateStore(input: DeactivateStoreInput) {
        return this.graphDotnetService.mutate<MediaclipMutationDeactivateStoreAndScheduleDeletionOfUserDataArgs>(graphql`
                mutation DeactivateStoreAndScheduleDeletionOfUserData($input: DeactivateStoreInput!){
                    deactivateStoreAndScheduleDeletionOfUserData(input: $input)
                }
            `, { input: input }
        ).pipe(map(r => r.deactivateStoreAndScheduleDeletionOfUserData));
    }

    deactivateStoreForMigration(input: DeactivateStoreInput) {
        return this.graphDotnetService.mutate<MediaclipMutationDeactivateStoreForMigrationArgs>(graphql`
                mutation DeactivateStoreForMigration($input: DeactivateStoreInput!){
                    deactivateStoreForMigration(input: $input)
                }
            `, { input: input }
        ).pipe(map(r => r.deactivateStoreForMigration));
    }

    activateStoreAfterMigration(input: ActivateStoreInput) {
        return this.graphDotnetService.mutate<MediaclipMutationActivateStoreAfterMigrationArgs>(graphql`
                mutation ActivateStoreAfterMigration($input: ActivateStoreInput!){
                    activateStoreAfterMigration(input: $input)
                }
            `, { input: input }
        ).pipe(map(r => r.activateStoreAfterMigration));
    }

    setStoreToMigrated(param: { id: string, region: string }) {
        return this.graphDotnetService.mutate<MediaclipMutationSetStoreToMigratedArgs>(graphql`
                mutation SetStoreToMigrated($input: SetStoreToMigratedInput!){
                    setStoreToMigrated(input: $input)
                }
            `, { input: param }
        ).pipe(map(r => r.setStoreToMigrated));
    }

    updateStoreConfig(input: UpdateStoreConfigInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateStoreConfigArgs>(graphql`
                mutation updateStoreConfig($input: UpdateStoreConfigInput!) {
                    updateStoreConfig(input: $input)
                }
            `, { input: input }
        ).pipe(map(r => r.updateStoreConfig));
    }
}
